/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import PosDayOffItem from './PosDayOffItem'
import React, { useEffect, useState } from 'react'
import { Button, Divider, Header, Icon, Segment } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { notify } from 'react-notify-toast'
import { getPosOffDays, setPosOffDays } from '../../../../services/PointOfSale/PointOfSale.service'
import { Prompt } from 'react-router-dom'
import { isAfter, isValid } from 'date-fns'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { sortDesc } from '../../../../helpers/array'

const itemStyles = css`
    display: flex;
    column-gap: 1rem;
`

const sortDaysOff = (daysOff) => {
    return sortDesc(daysOff,  d => new Date(d.from));
}

const PosDaysOff = ({pos}) => {
    const [t] = useTranslation()

    const initialPosDayOffToAdd = {
        from: null,
        to: null,
        errors: []
    }

    const [posDayOffToAdd, setPosDayOffToAdd] = useState(initialPosDayOffToAdd)

    const {data:posDaysOff,isFetching,isLoading} = useQuery({
        queryKey: ['pos', pos.id,'daysOff'],
        queryFn: async () => {
            const daysOff = await  getPosOffDays(pos.id);
            const sorted = sortDaysOff(daysOff);
            return sorted ?? [];
        },
        refetchOnWindowFocus: false,
        initialData: [],
        enabled: !!pos.id,
    });

    const queryClient = useQueryClient()

    const handleAddPosDayOff = useMutation({
        mutationFn: async () => {
           await setPosOffDays(pos.id, [posDayOffToAdd,...posDaysOff].map(offDay => ({
                from: offDay.from,
                to: offDay.to,
           })))
        },
        onMutate: () => {
            queryClient.setQueryData(['pos', pos.id,'daysOff'], (prevData) => (sortDaysOff([...prevData, posDayOffToAdd])))
        },
        onSuccess: async () => {
            notify.show(t('global.registerSuccess'), 'success');
            setPosDayOffToAdd(initialPosDayOffToAdd);
        },
        onError: (error) => {
            notify.show(error.message, 'error')
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ['pos', pos.id,'daysOff'] });
        }
    })

    const handleRemovePosDayOff = useMutation({
        mutationFn: async (posDayOffIndex) => {
            const newArray = [...posDaysOff]
            newArray.splice(posDayOffIndex, 1)
           await setPosOffDays(pos.id, newArray.map(offDay => ({
                from: offDay.from,
                to: offDay.to,
           })))
        },
        onMutate: (posDayOffIndex) => {
            queryClient.setQueryData(['pos', pos.id,'daysOff'], (prevData) => {
                const newData = [...prevData]
                newData.splice(posDayOffIndex, 1)
                return sortDaysOff(newData)
            })
        },
        onSuccess: async () => {
            notify.show(t('global.registerSuccess'), 'success');
        },
        onError: (error) => {
            notify.show(error.message, 'error')
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ['pos', pos.id,'daysOff'] });
        }
    })


    const canAddPosDayOff = () => {
        return !posDayOffToAdd.errors?.from
          && !posDayOffToAdd.errors?.to
          && isValid(new Date(posDayOffToAdd.from))
          && isValid(new Date(posDayOffToAdd.to))
          && isAfter(new Date(posDayOffToAdd.to), new Date(posDayOffToAdd.from))
    }


    const handlePosDaysOffChange = (posDayOff) => {
        setPosDayOffToAdd(prev => ({
            ...prev,
            ...posDayOff,
        }))
    }

    const handleOnPosDayOffToAddError = (fieldName, reason) => {
        setPosDayOffToAdd(prev => ({
            ...prev,
            errors: {
                ...prev.errors,
                [fieldName]: reason
            }
        }))
    }

    const isModifying = handleAddPosDayOff.isPending || handleRemovePosDayOff.isPending;


    return (
      <>
          <span css={itemStyles}>
                <PosDayOffItem
                  from={posDayOffToAdd.from}
                  to={posDayOffToAdd.to}
                  onChange={handlePosDaysOffChange}
                  onError={handleOnPosDayOffToAddError}
                  readonly={isModifying}
                />
                <Button loading={isModifying || isFetching} icon color="teal" disabled={!canAddPosDayOff() || isModifying} onClick={handleAddPosDayOff.mutate}>
                    <Icon name="add"/>
                </Button>
            </span>

          <Divider/>

          <div
            css={css`
                display: flex;
                flex-direction: column;
                gap: 1rem;
            `}
          >

            <Segment loading={isLoading}  basic css={css`padding-left:0!important; padding-top:0!important; `}>
               {posDaysOff.length === 0 &&
                <Header textAlign='center'

                  icon
                >
                    <Icon color="teal" name="calendar times outline"/>
                    {t('administration.pos_days_off.no_days_off')}
                </Header>
              }
                {posDaysOff.map((posDayOff, index) =>
                                <span key={`posDaysOff-${index}`} css={css`${itemStyles}  ${css`padding-bottom: 0.5rem;`}`}>
                                        <PosDayOffItem readonly from={posDayOff.from} to={posDayOff.to}/>
                                        <Button icon onClick={() => handleRemovePosDayOff.mutate(index)} loading={isModifying || isFetching}>
                                            <Icon name="minus"/>
                                        </Button>
                                    </span>,
                            )}
            </Segment>

          </div>

      </>
    )
}

export default PosDaysOff
